import { AIShotGenerationCharacterVariantSelection, Character, NodeType, SerializedContent } from '@storyverse/shared'

const normalizeText = (value: string) => value.trim().toLowerCase()

const buildLookSummary = (variant: NonNullable<Character['variants']>[number]) => {
    const details = [
        variant.role,
        variant.age ? `${variant.age} years old` : '',
        variant.hair,
        variant.clothing_style,
        variant.current_emotional_state,
    ].filter(Boolean)

    return details.join(', ')
}

/**
 * Resolves scene character mentions to real character + variant records from the DB.
 * We do this in the frontend because it has access to canonical project data, which lets
 * us preserve stable character and variant IDs instead of inventing placeholders later.
 */
export const inferCharacterVariantsFromScene = (
    sceneContent: SerializedContent[],
    characters: Character[]
): AIShotGenerationCharacterVariantSelection[] => {
    if (!sceneContent.length || !characters.length) return []

    const sceneCharacterMentions = new Set<string>()

    sceneContent.forEach((node) => {
        if (node.type !== NodeType.Character) return

        const mention = node.children?.map((child) => child.text || '').join(' ').trim()
        if (mention) {
            sceneCharacterMentions.add(normalizeText(mention))
        }
    })

    return characters.flatMap((character) => {
        const canonicalNames = [
            character.fullname,
            character.nickname || '',
            ...(character.fullname ? [character.fullname.toUpperCase()] : []),
        ]
            .filter(Boolean)
            .map(normalizeText)

        const isMentioned = canonicalNames.some((name) => sceneCharacterMentions.has(name))
        if (!isMentioned) return []

        const selectedVariant =
            character.variants?.find((variant) => variant.isDefault) || character.variants?.[0]

        if (!selectedVariant) return []

        return [
            {
                characterId: character.id,
                characterName: character.fullname,
                variantId: selectedVariant.id,
                variantLabel: selectedVariant.label,
                role: selectedVariant.role,
                age: selectedVariant.age,
                look: buildLookSummary(selectedVariant),
            },
        ]
    })
}
